import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  School as SchoolIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Launch as LaunchIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';

const LeadershipDevelopment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const resourceSegments = [
    {
      title: 'General',
      url: '/library/bundles/ecf92067b13b4955b181949f124bc0de',
      icon: <AssignmentIcon />,
      description: 'General leadership development resources and materials',
    },
    {
      title: 'LDC Resource Library Index',
      url: '/library/bundles/017fbf8fa70842f88e0e3afc3d862ac7',
      icon: <SchoolIcon />,
      description: 'Complete index of all leadership development resources',
    },
    {
      title: 'Getting to Know Your Sisters!',
      url: '/library/bundles/1e7ea2a0b2cd406397fe84037e302d04',
      icon: <GroupIcon />,
      description: 'Building relationships and sisterhood connections',
    },
    {
      title: 'Goal Setting',
      url: '/library/bundles/c5205773a1854c3184752ee7759a2f66',
      icon: <TrendingUpIcon />,
      description: 'Strategic planning and goal achievement techniques',
    },
    {
      title: 'Leadership for Local Chapter Officers',
      url: '/library/bundles/7219f3c96c7a4f3b9c2c65c5b7ed942d',
      icon: <EmojiEventsIcon />,
      description: 'Essential leadership skills for chapter officers',
    },
    {
      title: 'Leadership for S/P/D Officers',
      url: '/library/bundles/0c566286bec348258e8d12f09ea5195c',
      icon: <StarIcon />,
      description: 'Advanced leadership for state/provincial/district officers',
    },
    {
      title: 'Managing Conflict',
      url: '/library/bundles/5f9cbe461db945bfacf2ba959203ffa8',
      icon: <PsychologyIcon />,
      description: 'Conflict resolution and communication strategies',
    },
    {
      title: 'Personal Development',
      url: '/library/bundles/017fbf8fa70842f88e0e3afc3d862ac7',
      icon: <TrendingUpIcon />,
      description: 'Personal growth and self-improvement resources',
    },
    {
      title: 'Reach for the Stars',
      url: '/library/bundles/c6d99e37962241ed9e5385790ec089ef',
      icon: <StarIcon />,
      description:
        'Create the Leadership Team your chapter deserves! Follow the path to International Chapter President.',
    },
  ];

  const handleResourceClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #fff 0%, #8C4799 100%)`,
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={10}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: isMobile ? '2.5rem' : '3.5rem',
                  lineHeight: 1.2,
                }}
              >
                Leadership Development
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  maxWidth: '1000px',
                }}
              >
                Membership in P.E.O. gives women the opportunity to build their
                leadership skills. Whether it’s leading within your local
                chapter or at a higher level in the organization, you can be
                confident in knowing you have the support of your sisters who
                want you to succeed and flourish.
              </Typography>
              <Chip
                label="P.E.O. Leadership Development Committee"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '1rem',
                  py: 1,
                  px: 2,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Main Content */}
        <Grid container spacing={6}>
          {/* Left Column - About Section */}
          <Grid item xs={12} lg={4}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="img"
                    src="https://www.peointernational.org/wp-content/uploads/2023/10/Copy-of-LDC_InterimLogo_Primary.png"
                    alt="LDC Logo"
                    sx={{
                      width: '150px',
                      height: 'auto',
                      opacity: 0.8,
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ mb: 3, color: '#A6192E', fontWeight: 600 }}
                >
                  Our Mission
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  The mission of the P.E.O. Leadership Development Committee is
                  to provide educational, supportive and enriching leadership
                  resources for current and potential leaders at the local,
                  state/provincial/district and International levels.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ mb: 3, color: '#5D285F', fontWeight: 600 }}
                >
                  Why Leadership Development?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Membership in P.E.O. gives women the opportunity to build
                  their leadership skills. Whether it's leading within your
                  local chapter or at a higher level in the organization, you
                  can be confident in knowing you have the support of your
                  sisters who want you to succeed and flourish.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  The skills you build as a P.E.O. member – including public
                  speaking, project planning, working as part of a team and goal
                  setting – transfer to other areas in your life. P.E.O. helps
                  you become stronger in your career, your family and any other
                  organizations you are a part of.
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: '#8C4799', fontWeight: 600 }}
                >
                  How to Use These Resources
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Select a resource button below to see the Leadership
                  Development Resources related to that topic. A PDF will open
                  that lists all resources for that topic. Select a resource and
                  it will take you right to that resource in the Resource
                  Library.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Resource Segments */}
          <Grid item xs={12} lg={8}>
            <Typography
              variant="h4"
              component="h3"
              sx={{ mb: 4, color: '#5D285F', fontWeight: 600 }}
            >
              Leadership Resources
            </Typography>
            <Grid container spacing={3}>
              {resourceSegments.map((resource, index) => (
                <Grid
                  item
                  xs={12}
                  sm={resource.title.includes('Reach for the Stars') ? 12 : 6}
                  key={index}
                >
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        border: `2px solid ${
                          resource.title.includes('General')
                            ? '#A6192E'
                            : resource.title.includes('LDC Resource')
                              ? '#5D285F'
                              : resource.title.includes('Reach for the Stars')
                                ? '#A6192E'
                                : '#8C4799'
                        }`,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 2.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: resource.title.includes(
                          'Reach for the Stars'
                        )
                          ? 'row'
                          : 'column',
                        alignItems: resource.title.includes(
                          'Reach for the Stars'
                        )
                          ? 'center'
                          : 'stretch',
                      }}
                    >
                      {resource.title.includes('Reach for the Stars') ? (
                        <>
                          {/* Left side - Content */}
                          <Box
                            sx={{
                              flex: 1,
                              pr: 3,
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  color: '#5D285F',
                                  mr: 2,
                                }}
                              >
                                {resource.icon}
                              </Box>
                              <Typography
                                variant="h6"
                                component="h4"
                                sx={{ fontWeight: 600, flex: 1 }}
                              >
                                {resource.title}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                lineHeight: 1.6,
                                color: 'text.secondary',
                                flex: 1,
                              }}
                            >
                              {resource.description}
                            </Typography>
                            <Box sx={{ mt: 'auto', pt: 1, pb: 0.5 }}>
                              <Button
                                variant="contained"
                                endIcon={<LaunchIcon />}
                                onClick={() =>
                                  handleResourceClick(resource.url)
                                }
                                sx={{
                                  backgroundColor: '#5D285F',
                                  color: 'white',
                                  py: 1.5,
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    backgroundColor: '#4A1F4C',
                                  },
                                }}
                                fullWidth
                              >
                                Learn More
                              </Button>
                            </Box>
                          </Box>
                          {/* Right side - Image */}
                          <Box
                            sx={{
                              flex: 1,
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="img"
                              src="https://www.peointernational.org/wp-content/uploads/2023/11/Leadership-in-P.E.O.-pyramid-1536x1152.jpg"
                              alt="Leadership in P.E.O. Pyramid"
                              sx={{
                                width: '100%',
                                maxWidth: '280px',
                                height: 'auto',
                                borderRadius: 2,
                                boxShadow: 2,
                              }}
                            />
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                color: '#5D285F',
                                mr: 2,
                              }}
                            >
                              {resource.icon}
                            </Box>
                            <Typography
                              variant="h6"
                              component="h4"
                              sx={{ fontWeight: 600, flex: 1 }}
                            >
                              {resource.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              flex: 1,
                              lineHeight: 1.6,
                              color: 'text.secondary',
                            }}
                          >
                            {resource.description}
                          </Typography>
                          <Box sx={{ mt: 'auto', pt: 1, pb: 0.5 }}>
                            <Button
                              variant="contained"
                              endIcon={<LaunchIcon />}
                              onClick={() => handleResourceClick(resource.url)}
                              sx={{
                                backgroundColor: '#5D285F',
                                color: 'white',
                                py: 1.5,
                                fontWeight: 'bold',
                                '&:hover': {
                                  backgroundColor: '#4A1F4C',
                                },
                              }}
                              fullWidth
                            >
                              Access Resource
                            </Button>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Lead Your Way Section */}
        <Box sx={{ mt: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              background: `linear-gradient(135deg, #fff 0%, #A6192E 100%)`,
              color: 'white',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h4"
              component="h3"
              sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}
            >
              Lead Your Way
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, textAlign: 'center', opacity: 0.9 }}
            >
              A benefit of P.E.O. is that it grows with you! You can get more
              involved or stay at the same pace. As you grow with P.E.O., you'll
              find your personal connection to the Sisterhood grows as well.
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Present a Chapter Program
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Share your special talent, talk about a hobby or choose a
                    ready-to-go chapter program from the Resource Library.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Invite a Guest
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    You must be a P.E.O. for at least one year before you can
                    sponsor someone for membership, but you can always invite
                    guests to socials and other events.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Reach Out to Members
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Make your sisters feel valued! Think of ways to help all
                    members feel included.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <EmojiEventsIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Host Chapter Meetings
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Work with another sister to ensure everything is ready for
                    your local chapter meeting.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LeadershipDevelopment;
